// src/modules/sales/hooks/saleFormHelpers.js
// Satış formu yardımcı fonksiyonları

import { doc, collection, serverTimestamp, writeBatch, increment, Timestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';

// Helper for safe timestamp conversion
export const safeTimestamp = (val) => {
    if (!val) return null;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
};

/**
 * Yeni satış oluşturma batch işlemi
 */
export const createNewSale = async (salesData, customer, satilanUrunler, toplamTutar, odenenTutar, odemeTipi, isDeviceSale, deviceData) => {
    const batch = writeBatch(db);

    // 1. Satış Kaydı Oluştur
    const saleRef = doc(collection(db, "sales"));
    batch.set(saleRef, salesData);

    // 2. Borç Kaydı Oluştur
    const debtRef = doc(collection(db, "transactions"));
    batch.set(debtRef, {
        customerId: customer.id,
        customerName: customer.adSoyad,
        type: "borc",
        amount: toplamTutar,
        description: `Ürün Satışı: #${saleRef.id} - ${satilanUrunler.map(u => u.ad || u.urunAdi).join(', ')}`,
        timestamp: serverTimestamp(),
        refId: saleRef.id,
        refType: 'sale'
    });

    // 3. Tahsilat Kaydı Oluştur
    if (Number(odenenTutar) > 0) {
        const paymentRef = doc(collection(db, "transactions"));
        batch.set(paymentRef, {
            customerId: customer.id,
            customerName: customer.adSoyad,
            type: "tahsilat",
            amount: Number(odenenTutar),
            paymentMethod: odemeTipi,
            description: `Satış Peşinatı: #${saleRef.id}`,
            timestamp: serverTimestamp(),
            refId: saleRef.id,
            refType: 'sale'
        });
    }

    // 4. Müşteri Bakiyesini Güncelle
    const netBalanceChange = toplamTutar - Number(odenenTutar);
    if (netBalanceChange !== 0) {
        const customerRef = doc(db, "customers", customer.id);
        batch.update(customerRef, {
            bakiye: increment(netBalanceChange)
        });
    }

    // 5. Stok Düşür
    for (const item of satilanUrunler) {
        if (item.tip === 'urun' && item.referansId) {
            const stockRef = doc(db, "stocks", item.referansId);
            batch.update(stockRef, {
                stokAdedi: increment(-item.miktar)
            });
        }
    }

    // 6. Cihaz Kaydı Oluştur
    if (isDeviceSale && deviceData) {
        const deviceRef = doc(collection(db, "devices"));
        batch.set(deviceRef, {
            ...deviceData,
            createdAt: serverTimestamp(),
            satisRefId: saleRef.id
        });
    }

    await batch.commit();
};

/**
 * Mevcut satış güncelleme batch işlemi
 */
export const updateExistingSale = async (id, salesData, customer, toplamTutar, odenenTutar, odemeTipi) => {
    const batch = writeBatch(db);
    const docRef = doc(db, "sales", id);

    // 1. Satış Kaydını Güncelle
    // durum ve olusturmaTarihi düzenlemede DEĞİŞTİRİLMEZ; böylece orijinal oluşturma
    // tarihi korunur. (undefined göndermek update()'i exception'a düşürür ve güncelleme
    // hiç kaydedilmezdi — bu yüzden alanları tamamen çıkarıyoruz.)
    const { durum, olusturmaTarihi, ...saleUpdateData } = salesData;
    batch.update(docRef, saleUpdateData);

    // 2. Finansal İşlemleri Bul ve Güncelle
    const transactionsRef = collection(db, "transactions");
    const qTrans = query(transactionsRef, where("refId", "==", id), where("refType", "==", "sale"));
    const transSnapshot = await getDocs(qTrans);

    let oldDebt = 0;
    let oldPaid = 0;
    let debtTransRef = null;
    let paymentTransRef = null;

    transSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.type === 'borc') {
            oldDebt += Number(data.amount) || 0;
            debtTransRef = docSnap.ref;
        }
        if (data.type === 'tahsilat') {
            oldPaid += Number(data.amount) || 0;
            paymentTransRef = docSnap.ref;
        }
    });

    // 3. Borç İşlemini Güncelle
    if (debtTransRef) {
        batch.update(debtTransRef, { amount: toplamTutar });
    } else {
        const newDebtRef = doc(collection(db, "transactions"));
        batch.set(newDebtRef, {
            customerId: customer.id,
            customerName: customer.adSoyad,
            type: "borc",
            amount: toplamTutar,
            description: `Ürün Satışı: #${id}`,
            timestamp: serverTimestamp(),
            refId: id,
            refType: 'sale'
        });
    }

    // 4. Tahsilat İşlemini Güncelle
    const newPaidAmount = Number(odenenTutar);
    if (paymentTransRef) {
        if (newPaidAmount > 0) {
            batch.update(paymentTransRef, { amount: newPaidAmount, paymentMethod: odemeTipi });
        } else {
            batch.delete(paymentTransRef);
        }
    } else if (newPaidAmount > 0) {
        const newPaymentRef = doc(collection(db, "transactions"));
        batch.set(newPaymentRef, {
            customerId: customer.id,
            customerName: customer.adSoyad,
            type: "tahsilat",
            amount: newPaidAmount,
            paymentMethod: odemeTipi,
            description: `Satış Peşinatı: #${id}`,
            timestamp: serverTimestamp(),
            refId: id,
            refType: 'sale'
        });
    }

    // 5. Müşteri Bakiyesini Güncelle (Delta)
    const oldNetEffect = oldDebt - oldPaid;
    const newNetEffect = toplamTutar - newPaidAmount;
    const balanceAdjustment = newNetEffect - oldNetEffect;

    if (balanceAdjustment !== 0) {
        const customerRef = doc(db, "customers", customer.id);
        batch.update(customerRef, {
            bakiye: increment(balanceAdjustment)
        });
    }

    await batch.commit();
};
